import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@crashx/ui";
import { useChatStore } from "./chatStore.js";
import { chatApi, chatError } from "./chatApi.js";
import { ChatMessageRow } from "./ChatMessageRow.js";
import { RainAnnouncement } from "./RainAnnouncement.js";

export function ChatHistory({ canModerate }: { canModerate: boolean }) {
  const { t } = useTranslation();
  const messages = useChatStore((state) => state.messages);
  const notices = useChatStore((state) => state.notices);
  const cursor = useChatStore((state) => state.nextCursor);
  const scroll = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (pinned.current && scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight;
  }, [messages, notices]);
  async function older() {
    if (!cursor || busy) return;
    const epoch = useChatStore.getState().epoch;
    const height = scroll.current?.scrollHeight ?? 0;
    setBusy(true);
    setError(null);
    pinned.current = false;
    try {
      const page = await chatApi.history(cursor);
      if (useChatStore.getState().epoch !== epoch) return;
      useChatStore.getState().history(page);
      requestAnimationFrame(() => {
        if (scroll.current) scroll.current.scrollTop += scroll.current.scrollHeight - height;
      });
    } catch (cause) {
      setError(chatError(cause));
    } finally {
      setBusy(false);
    }
  }
  const rows = [...messages.map((message) => ({ kind: "message" as const, data: message })), ...notices].sort(
    (a, b) => a.data.sentAt - b.data.sentAt || a.data.id.localeCompare(b.data.id),
  );
  return (
    <>
      {cursor ? (
        <Button variant="ghost" disabled={busy} onClick={() => void older()}>
          {t("chat.older")}
        </Button>
      ) : null}
      {error ? <p role="alert">{t(error)}</p> : null}
      <div
        ref={scroll}
        className="cx-chat-history"
        role="log"
        aria-label={t("chat.messages")}
        aria-live="polite"
        aria-relevant="additions"
        tabIndex={0}
        onScroll={() => {
          const box = scroll.current;
          if (box) pinned.current = box.scrollHeight - box.scrollTop - box.clientHeight < 60;
        }}
      >
        {rows.length === 0 ? <p className="text-muted">{t("chat.empty")}</p> : null}
        <ul className="m-0 list-none p-0">
          {rows.map((row) =>
            row.kind === "message" ? (
              <ChatMessageRow key={row.data.id} message={row.data} canModerate={canModerate} />
            ) : row.kind === "rain" ? (
              <RainAnnouncement key={row.data.id} rain={row.data} />
            ) : (
              <li key={row.data.id} className="cx-chat-message text-success">
                {t("chat.bigWin", {
                  nick: row.data.nick,
                  amount: row.data.amount,
                  multiplier: row.data.multiplier.toFixed(2),
                })}
              </li>
            ),
          )}
        </ul>
      </div>
    </>
  );
}
